import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { createGroup, getGroup } from '../lib/firestore/groups';
import { storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import '../styles/CreateGroup.css';

const CreateGroup = () => {
  const { user } = useAuthState();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get parent info from URL params (for creating subgroups)
  const parentTypeParam = searchParams.get('parentType');
  const parentIdParam = searchParams.get('parentId');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    parentType: parentTypeParam || 'profile',
    parentId: parentIdParam || user?.uid || '',
    visibility: 'invite_only',
    allowSubgroups: true,
    membersCanInvite: true,
    membersCanCreateEvents: false,
    groupRulesText: '',
    maxSponsorsPerMember: undefined,
    sponsorRequireOneEvent: false,
    sponsorAccountabilityEnabled: false,
    sponsorAccountabilityText: 'You are accountable for any member you invite. If a member breaks the rules you will be suspended from the group',
    groupChat: false,
  });

  const [groupImage, setGroupImage] = useState(null);
  const [groupImageUrl, setGroupImageUrl] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showAccountabilityModal, setShowAccountabilityModal] = useState(false);
  const [parentGroup, setParentGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load parent group info if creating a subgroup
  useEffect(() => {
    if (parentTypeParam === 'group' && parentIdParam) {
      loadParentGroup();
    }
  }, [parentTypeParam, parentIdParam]);

  const loadParentGroup = async () => {
    try {
      const group = await getGroup(parentIdParam);
      setParentGroup(group);
    } catch (error) {
      console.error('Error loading parent group:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!user) {
      alert('Please log in to upload images');
      return;
    }

    setUploadingImage(true);
    try {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }

      // Upload to Firebase Storage
      const fileRef = ref(storage, `group-images/${user.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      setGroupImageUrl(downloadURL);
      setGroupImage(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAccountabilityToggle = (checked) => {
    if (checked) {
      setShowAccountabilityModal(true);
    } else {
      setFormData({
        ...formData,
        sponsorAccountabilityEnabled: false,
        sponsorAccountabilityText: 'You are accountable for any member you invite. If a member breaks the rules you will be suspended from the group',
      });
    }
  };

  const handleSaveAccountabilityRules = () => {
    setFormData({
      ...formData,
      sponsorAccountabilityEnabled: true,
    });
    setShowAccountabilityModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setError('You must be logged in to create a group');
      return;
    }

    setError('');
    setLoading(true);

    try {
      console.log('🔵 [CreateGroup] Starting form submission');
      console.log('🔵 [CreateGroup] formData.description:', formData.description);
      console.log('🔵 [CreateGroup] formData.groupChat:', formData.groupChat);
      
      // Build group data object, only including defined values
      // Ensure parentId is valid - use user.uid if not set
      const parentId = formData.parentId || user.uid;
      if (!parentId) {
        throw new Error('Parent ID is required');
      }
      
      console.log('🔵 [CreateGroup] parentId value:', parentId);
      console.log('🔵 [CreateGroup] parentId type:', typeof parentId);
      
      const groupData = {
        ownerId: user.uid,
        parentType: formData.parentType,
        parentId: parentId,
        name: formData.name,
        visibility: formData.visibility,
        allowSubgroups: formData.allowSubgroups,
        rules: {
          membersCanInvite: formData.membersCanInvite,
          membersCanCreateEvents: formData.membersCanCreateEvents,
          sponsorAccountabilityEnabled: formData.membersCanInvite && formData.sponsorAccountabilityEnabled,
        },
        groupChat: {
          enabled: formData.groupChat,
        },
      };

      // Only add optional fields if they have truthy values (not null, undefined, or empty string)
      if (formData.description && formData.description.trim()) {
        groupData.description = formData.description.trim();
        console.log('🔵 [CreateGroup] Added description:', groupData.description);
      } else {
        console.log('🔵 [CreateGroup] NOT adding description (empty or undefined)');
      }
      if (formData.website && formData.website.trim()) {
        let url = formData.website.trim();
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url;
        }
        groupData.website = url;
      }
      // Explicitly check for image - only add if it's a valid non-empty string
      if (groupImageUrl && typeof groupImageUrl === 'string' && groupImageUrl.trim().length > 0) {
        groupData.image = groupImageUrl.trim();
      }
      // DO NOT add image field if it's null, undefined, or empty
      if (formData.groupRulesText && formData.groupRulesText.trim()) {
        groupData.rules.groupRulesText = formData.groupRulesText.trim();
      }
      if (formData.membersCanInvite && formData.maxSponsorsPerMember !== undefined && formData.maxSponsorsPerMember !== null) {
        groupData.rules.maxSponsorsPerMember = formData.maxSponsorsPerMember;
      }
      if (formData.membersCanInvite && formData.sponsorRequireOneEvent) {
        groupData.rules.sponsorEligibility = { minAttended: 1, requireOneEvent: true };
      }
      if (formData.membersCanInvite && formData.sponsorAccountabilityEnabled && formData.sponsorAccountabilityText && formData.sponsorAccountabilityText.trim()) {
        groupData.rules.sponsorAccountabilityText = formData.sponsorAccountabilityText.trim();
      }

      // Remove ALL undefined, null, and empty string values before sending
      // BUT keep boolean false values and objects with boolean false values
      const cleanGroupData = {};
      Object.keys(groupData).forEach(key => {
        const value = groupData[key];
        if (value !== undefined && value !== null && value !== '') {
          if (typeof value === 'object' && !Array.isArray(value) && value !== null && !(value instanceof Date)) {
            const cleanedNested = {};
            Object.keys(value).forEach(nestedKey => {
              const nestedValue = value[nestedKey];
              // Keep boolean false, but remove undefined, null, and empty strings
              if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '' || nestedValue === false || nestedValue === true) {
                cleanedNested[nestedKey] = nestedValue;
              }
            });
            // Only add nested object if it has at least one key
            if (Object.keys(cleanedNested).length > 0) {
              cleanGroupData[key] = cleanedNested;
            }
          } else {
            cleanGroupData[key] = value;
          }
        }
      });

      console.log('🔵 [CreateGroup] Cleaned groupData before sending:', cleanGroupData);
      console.log('🔵 [CreateGroup] Has description?', 'description' in cleanGroupData);
      console.log('🔵 [CreateGroup] Description value:', cleanGroupData.description);
      
      const groupId = await createGroup(cleanGroupData);
      console.log('🔵 [CreateGroup] Group created successfully:', groupId);

      // Navigate to the new group or parent group
      if (formData.parentType === 'group') {
        navigate(`/groups/${formData.parentId}`);
      } else {
        navigate(`/groups/${groupId}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to create group. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div 
      className="create-group-container"
      onClick={() => navigate(-1)}
    >
      <div 
        className="create-group-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="create-group-header">
          <div className="header-title-row">
            <div>
              <h1>Create Group</h1>
              <p>
                {parentGroup
                  ? `Creating subgroup in "${parentGroup.name}"`
                  : 'Start organizing with your people'}
              </p>
            </div>
            <div className="group-image-upload">
              <label className="group-image-label">
                {groupImageUrl ? (
                  <img src={groupImageUrl} alt="Group" className="group-image-preview" />
                ) : (
                  <div className="group-image-placeholder">
                    {uploadingImage ? '⋯' : '📷'}
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  disabled={uploadingImage}
                />
              </label>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="create-group-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="name">Group Name *</label>
            <input
              type="text"
              id="name"
              className="input"
              placeholder="e.g., Sunday Football, Book Club"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              className="input"
              rows={3}
              placeholder="What's this group about?"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              maxLength={500}
            />
          </div>

          <div className="form-group">
            <label htmlFor="website">Website (Optional)</label>
            <input
              type="url"
              id="website"
              className="input"
              placeholder="https://example.com"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            />
            <small className="form-hint">
              Link to group website or more info (https:// will be added automatically if missing)
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="visibility">Visibility *</label>
            <select
              id="visibility"
              className="input"
              value={formData.visibility}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  visibility: e.target.value,
                })
              }
              required
            >
              <option value="invite_only">Invite Only (Private)</option>
              <option value="invite_or_apply">Invite or Apply to Join</option>
              <option value="public">Public (Listed)</option>
            </select>
            <small className="form-hint">
              {formData.visibility === 'invite_only' &&
                'Only people with invite links can join'}
              {formData.visibility === 'invite_or_apply' &&
                'People can request to join or be invited'}
              {formData.visibility === 'public' &&
                'Anyone can discover and request to join'}
            </small>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.groupChat}
                onChange={(e) =>
                  setFormData({ ...formData, groupChat: e.target.checked })
                }
              />
              Allow Group Chat
            </label>
            <small className="form-hint">
              Enable a persistent group chat for all members to communicate anytime
            </small>
          </div>

          <div className="form-section">
            <h3>Group Rules</h3>
            <small className="form-hint" style={{ marginBottom: '1rem', display: 'block' }}>
              These can be changed at anytime
            </small>

            <div className="form-group">
              <label htmlFor="groupRulesText">Group Rules (Optional)</label>
              <textarea
                id="groupRulesText"
                className="input"
                rows={4}
                placeholder="Add your group rules here..."
                value={formData.groupRulesText}
                onChange={(e) =>
                  setFormData({ ...formData, groupRulesText: e.target.value })
                }
                maxLength={1000}
              />
              <small className="form-hint">
                Define rules and guidelines for your group members
              </small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.allowSubgroups}
                  onChange={(e) =>
                    setFormData({ ...formData, allowSubgroups: e.target.checked })
                  }
                />
                Allow Subgroups
              </label>
              <small className="form-hint">
                Members can create child groups within this group
              </small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.membersCanCreateEvents}
                  onChange={(e) =>
                    setFormData({ ...formData, membersCanCreateEvents: e.target.checked })
                  }
                />
                Members Can Create Events
              </label>
              <small className="form-hint">
                Allow members to create events in this group
              </small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.membersCanInvite}
                  onChange={(e) =>
                    setFormData({ ...formData, membersCanInvite: e.target.checked })
                  }
                />
                Members Can Sponsor Others
              </label>
              <small className="form-hint">
                Regular members can sponsor people to this group
              </small>
            </div>
          </div>

          {formData.membersCanInvite && (
            <div className="form-section">
              <h3>Sponsorship Rules</h3>

              <div className="form-group">
                <label htmlFor="maxSponsors">Max Sponsors Per Member</label>
                <input
                  type="number"
                  id="maxSponsors"
                  className="input"
                  min="0"
                  max="10"
                  placeholder="Leave blank for unlimited"
                  value={formData.maxSponsorsPerMember || ''}
                  onChange={(e) => {
                    const value = e.target.value ? parseInt(e.target.value) : undefined;
                    const clampedValue = value !== undefined && value > 10 ? 10 : value;
                    setFormData({
                      ...formData,
                      maxSponsorsPerMember: clampedValue,
                    });
                  }}
                />
                <small className="form-hint">
                  Limit how many people each member can sponsor (max 10)
                </small>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.sponsorRequireOneEvent}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sponsorRequireOneEvent: e.target.checked,
                      })
                    }
                  />
                  Members must attend one event before they can sponsor others
                </label>
                <small className="form-hint">
                  Sponsors must have attended at least one event
                </small>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.sponsorAccountabilityEnabled}
                    onChange={(e) => handleAccountabilityToggle(e.target.checked)}
                  />
                  Enable Sponsor Accountability
                </label>
                <small className="form-hint">
                  If sponsored member violates rules, sponsor is also suspended
                </small>
              </div>
            </div>
          )}

          {/* Accountability Rules Modal */}
          {showAccountabilityModal && (
            <div className="modal-overlay" onClick={() => setShowAccountabilityModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Sponsor Accountability Rules</h2>
                <p className="modal-hint">(edit to fit your policies)</p>
                <textarea
                  className="input"
                  rows={6}
                  value={formData.sponsorAccountabilityText}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sponsorAccountabilityText: e.target.value,
                    })
                  }
                  placeholder="Enter accountability rules..."
                />
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAccountabilityModal(false);
                      setFormData({
                        ...formData,
                        sponsorAccountabilityEnabled: false,
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveAccountabilityRules}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGroup;

